import { Footer, TenantHeader, TopBar } from '@/components/organisms';
import { ConsoleTemplate } from '@/components/templates/ConsoleTemplate';
import { isTenantPathAvailableInService } from '@/constants/routes';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { TenantDashboard } from '@/pages/main/dashboard/TenantDashboard';
import { NotFound } from '@/pages/main/system/NotFound';
import Sidebar, { persistSidebarOpen, readPersistedSidebarOpen } from '@/shared/layouts/Sidebar';
import { ModuleLoader } from '@runtime/ModuleLoader';
import { getTenantCustomization, getTenantCustomizations } from '@customize/registry';
import { useAuth } from '@shared/providers/AuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, RouteObject, useLocation, useParams } from 'react-router-dom';

import { ClinicDesignTemplatePage } from '@/pages/design-template';
import ContactEditPage from '@/pages/main/contact/ContactEditPage';
import ContactCompletePage from '@/pages/main/contact/ContactCompletePage';
import PatientCertificationPage from '@/pages/main/patient-certification';
import VisitSchedulePage from '@/pages/main/visitSchedule/VisitSchedulePage';
import DistanceChangePage from '@/pages/main/visitSchedule/DistanceChangePage';
import VisitClinicSchedulePage from '@/pages/main/visitClinic/VisitClinicSchedulePage';
import VisitClinicEditPage from '@/pages/main/visitClinic/VisitClinicEditPage';
import { ClinicInformationPage } from '@pages/main/clinic-information';
import { ClinicLocationDetailPage, ClinicLocationPage } from '@pages/main/clinicLocation';
import { CreateConsentPage } from '@pages/main/consent/pages/CreateConsentPage';
import { EditConsentPage } from '@pages/main/consent/pages/EditConsentPage';
import { TreatmentRecordConsentPrintPage } from '@pages/main/consent/pages/TreatmentRecordConsentPrintPage';
import { ConsentTreatmentPeriodListPage } from '@pages/main/consent/pages/ConsentTreatmentPeriodListPage';
import { DocumentTemplatePage } from '@pages/main/documentTemplate';
import { FreeMedicalPage } from '@pages/main/freeMedical';
import { HospitalPage } from '@pages/main/hospital';
// import { InsurerDetailPage, InsurerSearchPage } from '@pages/main/insurer';
import { InsurerAttentionPage } from '@pages/main/insurerAttention';
import { LongTermFrequentListPage } from '@pages/main/longTermFrequent/pages/LongTermFrequentListPage';
import { LongTermFrequentCreatePage } from '@pages/main/longTermFrequent/pages/LongTermFrequentCreatePage';
import LongTermFrequentEditPage from '@pages/main/longTermFrequent/pages/LongTermFrequentEditPage';
import { MassagerPage } from '@pages/main/massager';
import { MedicalExpensePricePage } from '@pages/main/medicalExpensePrices';
import { OrderSheetPage } from '@pages/main/orderSheet';
import {
  PatientCheckinMailsPage,
  PatientCheckinMailCreatePage,
  PatientCheckinMailEditPage,
  PatientContinuationAddPage,
  PatientContinuationEditPage,
  PatientContinuationPage,
  PatientEditPage,
  PatientHistoriesPage,
} from '@pages/main/patient';
import {
  PatientAddressPage,
  CreatePatientAddressPage,
  EditPatientAddressPage,
} from '@pages/main/patientAddresses';
import { RezeptApplicationCountListPage } from '@pages/main/rezeptApplicationCount/pages/RezeptApplicationCountListPage';
import { SheetHistoriesPage } from '@pages/main/sheetHistories';
import { ConsentListPage } from '@/pages/main/consent/pages/ConsentListPage';
import { TreatmentReportListPage } from '@/pages/main/treatmentReport/pages/TreatmentReportListPage';
import { CreateTreatmentReportPage } from '@/pages/main/treatmentReport/pages/CreateTreatmentReportPage';
import { EditTreatmentReportPage } from '@/pages/main/treatmentReport/pages/EditTreatmentReportPage';
import HomeVisitPatientPage from '@/pages/main/homeVisitPatient/HomeVisitPatientPage';
import HomeVisitMassagerPage from '@/pages/main/homeVisitMassager/HomeVisitMassagerPage';
import VisitPatientPage from '@/pages/main/visitPatient/VisitPatientPage';
import SearchPatientListPage from '@/pages/main/searchPatientList/SearchPatientListPage';
import VisitClinicInvoicesPage from '@/pages/main/rezepts/pages/VisitClinicInvoicesPage';
import ReturnInitPage from '@/pages/main/rezepts/pages/ReturnInitPage';
import TreatmentRecordListPage from '@/pages/main/treatmentRecord/pages/TreatmentRecordListPage';
import RezeptInvoiceListPage from '@/pages/main/rezeptsInvoice/RezeptInvoiceListPage';
import RezeptHistoriesListPage from '@/pages/main/rezeptHistories/pages/RezeptHistoriesListPage';
import MassagerCsvPage from '@/pages/main/massagerCsv/MassagerCsvPage';
import RezeptListPage from '@/pages/main/rezepts/pages/RezeptListPage';
import VisitCheckResultPage from '@/pages/main/rezepts/pages/VisitCheckResultPage';
import StatisticalReportPage from '@/pages/main/rezepts/statistical/StatisticalReportPage';

const TenantServiceRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { tenant } = useParams<{ tenant: string }>();
  const { serviceType } = useTheme();

  if (
    (serviceType === SERVICE_TYPE_A2 || serviceType === SERVICE_TYPE_R2) &&
    !isTenantPathAvailableInService(location.pathname, serviceType)
  ) {
    return <Navigate to={tenant ? `/${tenant}` : '/'} replace />;
  }

  return <>{children}</>;
};

/**
 * Tenant Router Layout Component
 * Wraps all tenant routes with the console template, sidebar, header, and footer
 */
const DefaultTenantShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(readPersistedSidebarOpen);
  useEffect(() => {
    persistSidebarOpen(sidebarOpen);
  }, [sidebarOpen]);

  return (
    <ConsoleTemplate
      sidebarOpen={sidebarOpen}
      header={<TenantHeader onMenuClick={() => setSidebarOpen(!sidebarOpen)} />}
      topBar={<TopBar sidebarOpen={sidebarOpen} />}
      sidebar={<Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
      footer={<Footer />}
    >
      {children}
    </ConsoleTemplate>
  );
};

export const TenantRouterLayout: React.FC = () => {
  const { tenant } = useParams<{ tenant: string }>();
  const { clinic } = useAuth();
  useTheme(); // Initialize theme context

  // A tenant may replace the whole chrome (topbar / sidebar / footer) from its
  // `customize/` bundle -- Hitowa serves the RAH shell here. Everyone else gets
  // the standard console.
  const Shell =
    getTenantCustomization(clinic?.site_type ?? tenant)?.clinicShell ?? DefaultTenantShell;

  return (
    <Shell>
      <TenantServiceRouteGuard>
        <Outlet />
      </TenantServiceRouteGuard>
    </Shell>
  );
};

/**
 * Renders a tenant's own screen only under that tenant's site type.
 *
 * Tenant routes are merged into one shared route table because the router tree
 * is built before the URL is known, so scoping has to happen at render time.
 */
const TenantScopedRoute: React.FC<{ code: string; children: React.ReactNode }> = ({
  code,
  children,
}) => {
  const { tenant } = useParams<{ tenant: string }>();
  const { clinic } = useAuth();
  const siteType = clinic?.site_type ?? tenant;

  if (siteType?.toLowerCase() !== code) return <NotFound />;

  return <>{children}</>;
};

const TenantIndexPage: React.FC = () => {
  const { tenant } = useParams<{ tenant: string }>();
  const { clinic } = useAuth();
  const IndexPage = getTenantCustomization(clinic?.site_type ?? tenant)?.clinicIndex;
  return IndexPage ? <IndexPage /> : <TenantDashboard />;
};

/** Clinic-portal routes contributed by `customize/`, each scoped to its tenant. */
const customizeRoutes: RouteObject[] = getTenantCustomizations().flatMap(tenant =>
  (tenant.clinicRoutes ?? []).map(route => ({
    ...route,
    element: <TenantScopedRoute code={tenant.code}>{route.element}</TenantScopedRoute>,
  }))
);

/**
 * Tenant Route Configuration
 * Defines all routes available within the tenant portal
 */
export const tenantRoutes: RouteObject[] = [
  {
    index: true,
    element: <TenantIndexPage />,
  },
  {
    path: '',
    element: <TenantIndexPage />,
  },
  {
    path: 'patients/edit',
    element: <PatientEditPage />,
  },
  {
    path: 'patients/search_patients',
    element: <SearchPatientListPage />,
  },
  {
    path: 'patients/copy/:copyFromId',
    element: <PatientEditPage />,
  },
  {
    path: 'patients/edit/:id',
    element: <PatientEditPage />,
  },
  {
    path: 'patients/continuation/add/:patientId/:searchTreatment/:sortRadio',
    element: <PatientContinuationAddPage />,
  },
  {
    path: 'patients/continuation/edit/:patientId/:searchYearmonth',
    element: <PatientContinuationEditPage />,
  },
  {
    path: 'patients/continuation',
    element: <PatientContinuationPage />,
  },
  {
    path: 'patients/:id/histories',
    element: <PatientHistoriesPage />,
  },
  {
    path: 'patients/:id/dminfo/edit',
    element: (
      <ModuleLoader
        module="patient"
        component="PatientDmInfoEdit"
        fallback={<div>Loading patient DM info...</div>}
      />
    ),
  },
  {
    path: 'patients/certification',
    element: <PatientCertificationPage />,
  },
  {
    path: 'patient_addresses/:id/list',
    element: <PatientAddressPage />,
  },
  {
    path: 'patient_addresses/:id/create',
    element: <CreatePatientAddressPage />,
  },
  {
    path: 'patient_addresses/:id/edit/:addressId',
    element: <EditPatientAddressPage />,
  },
  {
    path: 'long_term_frequents/:id/list',
    element: <LongTermFrequentListPage />,
  },
  {
    path: 'long_term_frequents/:id/edit/:recordId',
    element: <LongTermFrequentEditPage />,
  },
  {
    path: 'long_term_frequents/:id/create',
    element: <LongTermFrequentCreatePage />,
  },
  {
    path: 'long_term_frequents/:id/edit',
    element: <LongTermFrequentCreatePage />,
  },
  {
    path: 'checkin_mails/:id/list',
    element: <PatientCheckinMailsPage />,
  },
  {
    path: 'consents/treatment_period',
    element: <ConsentTreatmentPeriodListPage />,
  },
  {
    path: 'checkin_mails/:id/create',
    element: <PatientCheckinMailCreatePage />,
  },
  {
    path: 'checkin_mails/:id/edit/:mailId',
    element: <PatientCheckinMailEditPage />,
  },
  {
    path: 'consents/:patientId/list',
    element: <ConsentListPage />,
  },
  {
    path: 'consents/:patientId/create',
    element: <CreateConsentPage />,
  },
  {
    path: 'consents/:patientId/continue',
    element: <CreateConsentPage />,
  },
  {
    path: 'consents/:patientId/edit/:consentId',
    element: <EditConsentPage />,
  },
  {
    path: 'consents/:patientId/:consentId/treatment-record-print',
    element: <TreatmentRecordConsentPrintPage />,
  },
  {
    path: 'treatment-reports/:patientId/list',
    element: <TreatmentReportListPage />,
  },
  {
    path: 'treatment-reports/:patientId/create',
    element: <CreateTreatmentReportPage />,
  },
  {
    path: 'treatment-reports/:patientId/continue',
    element: <CreateTreatmentReportPage />,
  },
  {
    path: 'treatment-reports/:patientId/edit/:id',
    element: <EditTreatmentReportPage />,
  },
  {
    path: 'rezept_application_counts/:patientId/list',
    element: <RezeptApplicationCountListPage />,
  },
  {
    path: 'rezept_application_counts/:patientId/edit',
    element: <RezeptApplicationCountListPage />,
  },
  /** 施術録出力 (Treatment Record Output) — clinic-wide search + print modal, A2 + R2 */
  {
    path: 'treatment_records/init',
    element: <TreatmentRecordListPage />,
  },
  /** レセプト内容CSV出力 (Receipt Content List) — clinic search + CSV export via BottomBar (visible when rows exist) */
  {
    path: 'rezept_histories/init',
    element: <RezeptHistoriesListPage />,
  },
  {
    path: 'rezepts/init_massagers',
    element: <MassagerCsvPage />,
  },
  {
    path: 'rezepts',
    element: <div>Rezepts Module (Coming Soon)</div>,
  },
  /** 領収書・請求書 - Invoice / Receipt (entry point + list) */
  {
    path: 'rezepts/invoices_init',
    element: <RezeptInvoiceListPage />,
  },
  /** レセプト発行 - Receipt List (A2 + R2) */
  {
    path: 'rezepts/init',
    element: <RezeptListPage />,
  },
  /** 訪問データチェック結果 - Visit Data Check Result (BD: per legacy URL).
   *  Two routes registered so :visitmonth can be omitted when the user did not
   *  filter by treatment month (React Router v6 has no optional path params). */
  {
    path: 'rezepts/visit_check_ready/:ids/:yearmonth/:visitmonth',
    element: <VisitCheckResultPage />,
  },
  {
    path: 'rezepts/visit_check_ready/:ids/:yearmonth',
    element: <VisitCheckResultPage />,
  },
  /** [来院用]領収書(窓口) - Front Desk Receipt (In-Clinic) */
  {
    path: 'rezepts/visit_clinic_invoices_init',
    element: <VisitClinicInvoicesPage />,
  },
  /** 返戻・問合せレセプト登録 - Returned Receipt Registration */
  {
    path: 'rezepts/return_init',
    element: <ReturnInitPage />,
  },
  /** 統計一覧表 - Statistical Report (clinic monthly summary) */
  {
    path: 'rezepts/statistical_init',
    element: <StatisticalReportPage />,
  },
  {
    path: 'visits/schedule',
    element: <VisitSchedulePage />,
  },
  // Canonical: 3 segments {massagerId}/{clinicLocationId}/{visitdate} — pair (massager, location)
  // identifies the dropdown entry stable across admin edits.
  {
    path: 'visits/schedule/:massagerId/:clinicLocationId/:visitdate',
    element: <VisitSchedulePage />,
  },
  // Legacy 2-segment URL: cross-link from VisitClinicDetailModal or older bookmarks that
  // only carry massager_id (no location). The page picks the first location for that massager.
  {
    path: 'visits/schedule/:massagerId/:visitdate',
    element: <VisitSchedulePage />,
  },
  {
    path: 'visits/distance_change/:visitdate/:massagerId/:clinicLocationId',
    element: <DistanceChangePage />,
  },
  /** 来院 施術入力 (sidebar / patient nav 施術) — A2 + R2 */
  {
    path: 'visit_clinic/schedule',
    element: <VisitClinicSchedulePage />,
  },
  {
    path: 'visit_clinic/edit/:patientId/:yearmonth',
    element: <VisitClinicEditPage />,
  },
  {
    path: 'visits/patient_init',
    element: <HomeVisitPatientPage />,
  },
  {
    path: 'visits/massager_init',
    element: <HomeVisitMassagerPage />,
  },
  {
    path: 'visits/visitpatient_init',
    element: <VisitPatientPage />,
  },
  {
    path: 'visits',
    element: <div>Visits Module (Coming Soon)</div>,
  },
  // マスタメンテナンス
  {
    path: 'clinic_informations/init',
    element: <ClinicInformationPage />,
  },
  // {
  //   path: 'insurers/init',
  //   element: <InsurerSearchPage />,
  // },
  // {
  //   path: 'insurers/detail/:id',
  //   element: <InsurerDetailPage />,
  // },
  {
    path: 'insurer_attentions/init',
    element: <InsurerAttentionPage />,
  },
  {
    path: 'massagers/init',
    element: <MassagerPage />,
  },
  {
    path: 'hospitals/init',
    element: <HospitalPage />,
  },
  {
    path: 'document_templates/init',
    element: <DocumentTemplatePage />,
  },
  {
    path: 'clinic_locations/init',
    element: <ClinicLocationPage />,
  },
  {
    path: 'clinic_locations/:id',
    element: <ClinicLocationDetailPage />,
  },
  {
    path: 'free_medical/init',
    element: <FreeMedicalPage />,
  },
  {
    path: 'medical_expense_prices/init',
    element: <MedicalExpensePricePage />,
  },
  // レセプト用紙発注
  {
    path: 'sheets/order',
    element: <OrderSheetPage />,
  },
  {
    path: 'design-template',
    element: <ClinicDesignTemplatePage />,
  },
  {
    path: 'contact/edit',
    element: <ContactEditPage />,
  },
  {
    path: 'contact/complete',
    element: <ContactCompletePage />,
  },
  {
    path: 'sheets/histories',
    element: <SheetHistoriesPage />,
  },
  ...customizeRoutes,
  {
    path: '*',
    element: <NotFound />,
  },
];
